import { Link } from "react-router-dom";
import { colors } from "../../style";
import { apiPlaceholder, SENTIMENT } from "../../utils/meetingFormat";
import { Card, Chip, SectionLabel, StatusDot, ui } from "../ui";

// Meeting list rows: one card of day sections, each row a dot, a title with
// the time and sentiment on the right and a clamped detail line underneath.
// SentimentChip is shared with processed calls.

const style = {
  row: (on) => ({
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 14px",
    borderRadius: "10px",
    border: 0,
    textAlign: "left",
    font: "inherit",
    textDecoration: "none",
    color: "inherit",
    // undefined, not "none": inline styles would beat .sk-row:hover
    background: on ? colors.raised : undefined,
  }),
};

// Two lines, then an ellipsis
export const clamp = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

// groups: [{ key, title, items }]. A group without a title is shown without
// a heading (e.g. search hits with no dates).
export function DayGroups({ groups, renderItem, style: extra }) {
  return (
    <Card style={{ padding: "4px 6px 8px", ...extra }}>
      {groups.map((g) => (
        <section key={g.key} aria-label={g.title || undefined}>
          {g.title && (
            <SectionLabel style={{ padding: "12px 14px 4px" }}>
              {g.title}
            </SectionLabel>
          )}
          {g.items.map(renderItem)}
        </section>
      ))}
    </Card>
  );
}

// A Link with `to`, else a disabled row.
// dot: { color, title, pulse }
export function RecordRow({
  to,
  active,
  dot,
  title,
  meta,
  detail,
  detailColor = colors.textMuted,
  sentiment,
}) {
  const disabled = !to;
  const props = {
    className: disabled ? undefined : "sk-row",
    "aria-current": active ? "true" : undefined,
    style: {
      ...style.row(active),
      ...(disabled
        ? { opacity: 0.6, cursor: "default" }
        : { cursor: "pointer" }),
    },
  };

  const body = (
    <>
      {dot && (
        <StatusDot
          color={dot.color}
          title={dot.title}
          pulse={dot.pulse}
          style={{ marginTop: "7px" }}
        />
      )}
      <span style={{ display: "block", flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <strong
            style={{
              color: colors.text,
              fontSize: "14px",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </strong>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              flex: "none",
            }}
          >
            {meta && (
              <span
                style={{ ...ui.muted, fontSize: "12px", whiteSpace: "nowrap" }}
              >
                {meta}
              </span>
            )}
            {sentiment && <SentimentChip sentiment={sentiment} size="sm" />}
          </span>
        </span>
        {detail && (
          <span
            style={{
              ...clamp,
              fontSize: "13px",
              color: detailColor,
            }}
          >
            {detail}
          </span>
        )}
      </span>
    </>
  );

  if (to) {
    return (
      <Link to={to} {...props}>
        {body}
      </Link>
    );
  }
  return (
    <div aria-disabled="true" {...props}>
      {body}
    </div>
  );
}

// Mouth and brow strokes per face, drawn dark on a filled circle
const FACES = {
  angry: "M16 16.5s-1.5-2-4-2-4 2-4 2M6 6l3.5 1.5M18 6l-3.5 1.5",
  frown: "M16 16.5s-1.5-2-4-2-4 2-4 2",
  meh: "M8 15.5h8",
  smile: "M8 14s1.5 2.5 4 2.5 4-2.5 4-2.5",
  laugh: "M7 13.5h10a5 5 0 0 1-10 0Z",
};

// A filled face in the sentiment colour, so the mood reads at a glance
export function SentimentFace({ rating, size = 16 }) {
  const mood = SENTIMENT[rating];
  if (!mood) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="12" cy="12" r="11" fill={mood.color} />
      <g
        fill="none"
        stroke={colors.pageBackground}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 10.5h.01M15 10.5h.01" strokeWidth="2.6" />
        <path d={FACES[mood.face]} />
      </g>
    </svg>
  );
}

// sentiment: { rating, summary }. The summary shows as a tooltip, since the
// chip often sits inside a row link. Pass summary null to skip it.
// neutralIfUnknown: show Neutral for a missing or unknown rating.
export function SentimentChip({ sentiment, size, neutralIfUnknown }) {
  const rating =
    SENTIMENT[sentiment?.rating] || !neutralIfUnknown ? sentiment?.rating : 3;
  const mood = SENTIMENT[rating];
  if (!mood) return null;
  const note = apiPlaceholder(sentiment?.summary) ? null : sentiment?.summary;
  return (
    <Chip color={mood.color} title={note || undefined} size={size}>
      <SentimentFace rating={rating} size={size === "sm" ? 16 : 18} />
      {mood.label}
    </Chip>
  );
}
